// src/hooks/useBlogAdmin.js
import { useState, useCallback } from 'react';
import * as blogService from '../services/blogService';
import { useToast } from './useToast';

export const useBlogAdmin = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [currentFilters, setCurrentFilters] = useState({});
  const [seoScore, setSeoScore] = useState(null);
  const { showToast } = useToast();

  const fetchBlogs = useCallback(
    async (options = {}) => {
      setLoading(true);
      setError(null);
      try {
        const response = await blogService.getAdminBlogs(options);
        setBlogs(response.blogs || []);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            hasNext: false,
            hasPrev: false,
          },
        );
        setStats(
          response.stats || {
            total: 0,
            published: 0,
            draft: 0,
            scheduled: 0,
          },
        );
        setCurrentFilters(options);
        return response;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  const getBlogById = useCallback(
    async id => {
      setLoading(true);
      setError(null);
      try {
        const response = await blogService.getBlogById(id);
        return response.blog;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  // src/hooks/useBlogAdmin.js - Fix createBlog and updateBlog

  const createBlog = useCallback(
    async formData => {
      setLoading(true);
      setError(null);
      try {
        console.log('📤 Creating blog...');
        const response = await blogService.createBlog(formData);
        console.log('✅ Create response:', response);

        // Check if response indicates success
        if (response && response.success === false) {
          throw new Error(response.message || 'Failed to create blog');
        }

        showToast('Blog created successfully', 'success');
        await fetchBlogs(currentFilters);
        return response.blog || response;
      } catch (err) {
        console.error('❌ Create error:', err);
        const errorMessage = err.message || 'Failed to create blog';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  const updateBlog = useCallback(
    async (id, formData) => {
      setLoading(true);
      setError(null);
      try {
        console.log('📤 Updating blog:', id);
        const response = await blogService.updateBlog(id, formData);
        console.log('✅ Update response:', response);

        // Check if response indicates success
        if (response && response.success === false) {
          throw new Error(response.message || 'Failed to update blog');
        }

        showToast('Blog updated successfully', 'success');
        await fetchBlogs(currentFilters);
        return response.blog || response;
      } catch (err) {
        console.error('❌ Update error:', err);
        const errorMessage = err.message || 'Failed to update blog';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  const deleteBlog = useCallback(
    async id => {
      setLoading(true);
      setError(null);
      try {
        await blogService.deleteBlog(id);
        showToast('Blog deleted successfully', 'success');
        await fetchBlogs(currentFilters);
        return true;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  const bulkDelete = useCallback(
    async ids => {
      setLoading(true);
      setError(null);
      try {
        const result = await blogService.bulkDeleteBlogs(ids);
        showToast(
          result.message || `${ids.length} blog(s) deleted successfully`,
          'success',
        );
        await fetchBlogs(currentFilters);
        return result;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  const bulkUpdateStatus = useCallback(
    async (ids, status) => {
      setLoading(true);
      setError(null);
      try {
        const result = await blogService.bulkUpdateStatus(ids, status);
        showToast(
          result.message || `${ids.length} blog(s) updated to ${status}`,
          'success',
        );
        await fetchBlogs(currentFilters);
        return result;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  const toggleFeatured = useCallback(
    async (id, featured) => {
      setLoading(true);
      setError(null);
      try {
        const result = await blogService.toggleFeatured(id, featured);
        showToast(
          result.message ||
            `Blog ${featured ? 'featured' : 'unfeatured'} successfully`,
          'success',
        );
        await fetchBlogs(currentFilters);
        return result;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  // SEO Methods
  const getSEOScore = useCallback(
    async blogId => {
      setLoading(true);
      setError(null);
      try {
        const response = await blogService.getSEOScore(blogId);
        setSeoScore(response.score);
        return response.score;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  const autoGenerateSEO = useCallback(
    async blogId => {
      setLoading(true);
      setError(null);
      try {
        await blogService.autoGenerateSEO(blogId);
        showToast('SEO fields auto-generated successfully', 'success');
        await fetchBlogs(currentFilters);
        return true;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  const bulkUpdateSEO = useCallback(
    async (ids, seoData) => {
      setLoading(true);
      setError(null);
      try {
        const result = await blogService.bulkUpdateSEO(ids, seoData);
        showToast(
          result.message || `SEO updated for ${ids.length} blog(s)`,
          'success',
        );
        await fetchBlogs(currentFilters);
        return result;
      } catch (err) {
        setError(err.message);
        showToast(err.message, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchBlogs, currentFilters],
  );

  return {
    blogs,
    loading,
    error,
    stats,
    pagination,
    seoScore,
    fetchBlogs,
    getBlogById,
    createBlog,
    updateBlog,
    deleteBlog,
    bulkDelete,
    bulkUpdateStatus,
    toggleFeatured,
    getSEOScore,
    autoGenerateSEO,
    bulkUpdateSEO,
  };
};
